﻿import React, { useState } from 'react'
import { Text, Heading, Button, LoadingSpinner } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { 
  MegaphoneIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'

const GerarAudio = () => {
  const [mensagem, setMensagem] = useState('')
  const [velocidade, setVelocidade] = useState(0.9)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleGerarAudio = async () => {
    if (!mensagem.trim()) {
      setError('Por favor, informe uma mensagem')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      // Construir URL com query parameters
      const params = new URLSearchParams({
        velocidade: velocidade.toString(),
        mensagem: mensagem.trim()
      })
      
      const response = await fetch(`https://webhook.pasiai.com/webhook/GerarAudio?${params.toString()}`, {
        method: 'GET'
      })

      if (!response.ok) {
        throw new Error('Erro ao gerar áudio. Tente novamente.')
      }

      const data = await response.json()

      if (data.sucesso === true && data.url) {
        setSuccess(true)
        // Abrir URL automaticamente em nova aba
        window.open(data.url, '_blank')
      } else {
        throw new Error(data.mensagem || 'Erro ao gerar áudio. Resposta inválida.')
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar áudio. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  //Gerar opções de velocidade de 0.1 até 1.5 com step 0.1
  const velocidades = []
  for (let i = 0.1; i <= 1.5; i += 0.1) {
    velocidades.push(parseFloat(i.toFixed(1)))
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
        <PermissionGuard 
          moduleName="ia_gerar_audio" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Gerar Áudio
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                Gere áudio a partir de texto usando inteligência artificial
              </Text>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-2xl mx-auto">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
                  {/* Alerta sobre consumo de créditos */}
                  <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <Text size="sm" className="text-yellow-800 dark:text-yellow-300">
                      <strong>Atenção:</strong> A geração de áudio consome créditos e gera custos. Use com responsabilidade.
                    </Text>
                  </div>

                  {/* Mensagem */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Mensagem
                    </label>
                    <textarea
                      value={mensagem}
                      onChange={(e) => {
                        setMensagem(e.target.value)
                        setError(null)
                        setSuccess(false)
                      }}
                      placeholder="Digite a mensagem que deseja converter em áudio..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                      rows={8}
                      disabled={loading}
                    />
                    <Text size="xs" color="muted" className="mt-1">
                      A mensagem pode ter múltiplas linhas
                    </Text>
                  </div>

                  {/* Velocidade */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Velocidade
                    </label>
                    <select
                      value={velocidade}
                      onChange={(e) => {
                        setVelocidade(parseFloat(e.target.value))
                        setError(null)
                        setSuccess(false)
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={loading}
                    >
                      {velocidades.map((vel) => (
                        <option key={vel} value={vel}>
                          {vel}x
                        </option>
                      ))}
                    </select>
                    <Text size="xs" color="muted" className="mt-1">
                      Selecione a velocidade de reprodução (0.1x a 1.5x). <strong className="text-gray-700 dark:text-gray-300">Padrão: 0.9x</strong>
                    </Text>
                  </div>

                  {/* Mensagens de Erro/Sucesso */}
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                      <Text size="sm" className="text-red-600 dark:text-red-400">
                        {error}
                      </Text>
                    </div>
                  )}

                  {success && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <Text size="sm" className="text-green-600 dark:text-green-400">
                        Áudio gerado com sucesso! O link foi aberto em uma nova aba.
                      </Text>
                    </div>
                  )}

                  {/* Botão Gerar */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleGerarAudio}
                      disabled={loading || !mensagem.trim()}
                      className="flex items-center gap-2"
                      style={{ backgroundColor: BRAND_COLOR }}
                    >
                      {loading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <MegaphoneIcon className="h-5 w-5" />
                          Gerar Áudio
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </PermissionGuard>
    </div>
  )
}

export default GerarAudio

